import { motion, AnimatePresence } from 'framer-motion';
import PageError from '../components/PageError';
import {
  Users,
  User,
  GraduationCap,
  MapPin,
  Phone,
  AlertTriangle,
  XCircle,
  X,
  Search,
  Filter,
  RefreshCw,
  Edit,
  Trash2,
  UserPlus,
  Upload,
  Route,
  Home,
  MoreHorizontal,
  Download,
} from 'lucide-react';
import { exportToCsv } from '../utils/exportCsv';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdownMenu';
import StudentParentFormModal from '../components/StudentParentFormModal';
import AdvancedCSVImportModal from '../components/AdvancedCSVImportModal';
import StudentImpactConfirmationModal from '../components/StudentImpactConfirmationModal';
import BulkDeleteImpactModal from '../components/BulkDeleteImpactModal';
import useStudents from '../hooks/trStudents';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { useSortPaginate } from '../hooks/useSortPaginate';
import { SortableHeader, TablePagination, ColumnToggle, useColumnVisibility } from '../components/TableControls';
import { StatusBadge } from '../components/ui/StatusBadge';

const STUDENT_COLUMNS = [
  { key: 'classe', label: 'Classe' },
  { key: 'adresse', label: 'Adresse' },
  { key: 'transport', label: 'Transport' },
  { key: 'contact', label: 'Contact' },
  { key: 'status', label: 'Statut' },
];

const StudentManagementEnhanced = () => {
  const {
    students,
    filteredStudents,
    loading,
    error,
    filters,
    setFilters,
    showStudentModal,
    setShowStudentModal,
    selectedStudent,
    modalMode,
    selectedStudents,
    setSelectedStudents,
    bulkAction,
    setBulkAction,
    bulkLoading,
    showImportModal,
    setShowImportModal,
    showImpactModal,
    impactAction,
    selectedStudentForAction,
    loadData,
    handleAddStudent,
    handleEditStudent,
    handleStudentSubmit,
    handleDeleteStudent,
    executeAction,
    handleImportClick,
    handleImportComplete,
    handleBulkAction,
    confirmBulkDelete,
    cancelBulkDeleteModal,
    showBulkDeleteModal,
    toggleSelectAll,
    toggleSelectStudent,
    cancelImpactModal,
  } = useStudents();

  const { visibility, toggle, visible } = useColumnVisibility({
    classe: true,
    adresse: true,
    transport: true,
    contact: true,
    status: true,
  });

  const { paginated, page, totalPages, totalItems, pageSize, sortCol, sortDir, setPage, toggleSort } = useSortPaginate(
    filteredStudents,
    { col: 'firstName|first_name', dir: 'asc' },
    15,
    [filters],
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des Élèves</h1>
          <p className="text-gray-600 mt-1">
            Gestion unifiée des élèves, adresse indicative et prise en charge par arrêt
          </p>
        </div>
        <div className="hidden md:flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => {
              const isoToDMY = (d) => (d ? d.replace(/^(\d{4})-(\d{2})-(\d{2}).*/, '$3/$2/$1') : '');
              const primaryParent = (s) => s.parentsData?.find((p) => p.isPrimary) || s.parentsData?.[0];
              exportToCsv(
                filteredStudents,
                [
                  {
                    label: 'prenom_eleve',
                    value: (s) => s.firstName || s.first_name || '',
                  },
                  {
                    label: 'nom_eleve',
                    value: (s) => s.lastName || s.last_name || '',
                  },
                  {
                    label: 'date_naissance',
                    value: (s) => isoToDMY(s.dateOfBirth || s.date_of_birth),
                  },
                  {
                    label: 'genre',
                    value: (s) => (s.gender === 'male' ? 'M' : s.gender === 'female' ? 'F' : ''),
                  },
                  { label: 'niveau', value: (s) => s.grade_level || '' },
                  { label: 'classe', value: (s) => s.class_name || '' },
                  { label: 'adresse', value: (s) => s.pickup_address || '' },
                  {
                    label: 'prenom_parent',
                    value: (s) => primaryParent(s)?.firstName || '',
                  },
                  {
                    label: 'nom_parent',
                    value: (s) => primaryParent(s)?.lastName || '',
                  },
                  {
                    label: 'telephone_parent',
                    value: (s) => primaryParent(s)?.phone || '',
                  },
                  {
                    label: 'email_parent',
                    value: (s) => primaryParent(s)?.email || '',
                  },
                  {
                    label: 'relation_parent',
                    value: (s) => primaryParent(s)?.relationship || '',
                  },
                ],
                'eleves.csv',
              );
            }}
          >
            <Download size={16} />
            Exporter CSV
          </Button>
          <Button variant="outline" onClick={handleImportClick}>
            <Upload size={16} />
            Importer CSV
          </Button>
          <Button onClick={handleAddStudent}>
            <UserPlus size={16} />
            Ajouter élève + parent
          </Button>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Rechercher par nom, prénom, ID, transport..."
              value={filters.search}
              onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            />
            {filters.search && (
              <button
                onClick={() => setFilters((prev) => ({ ...prev, search: '' }))}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filtres:</span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {[
                {
                  value: '',
                  label: 'Tous',
                  active: 'bg-blue-100 text-blue-800',
                },
                {
                  value: 'active',
                  label: 'Actifs',
                  active: 'bg-green-100 text-green-800',
                },
                {
                  value: 'inactive',
                  label: 'Inactifs',
                  active: 'bg-gray-100 text-gray-800',
                },
              ].map(({ value, label, active }) => (
                <button
                  key={label}
                  onClick={() => setFilters((prev) => ({ ...prev, status: value }))}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    filters.status === value ? active : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              onClick={loadData}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Actualiser"
            >
              <RefreshCw size={16} />
            </button>
            <ColumnToggle columns={STUDENT_COLUMNS} visibility={visibility} onToggle={toggle} />
          </div>
        </div>
      </div>

      {/* Tableau / Cards */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {error && <PageError error={error} onRetry={loadData} variant="inline" />}
        {!error && (
          <>
            {/* ── Vue cartes (mobile < md) ── */}
            <div className="md:hidden divide-y divide-gray-100">
              {loading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <div key={`skel-m-${i}`} className="p-4 flex items-start gap-3">
                      <Skeleton className="h-10 w-10 rounded-full shrink-0" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-40" />
                        <Skeleton className="h-3 w-28" />
                        <Skeleton className="h-3 w-36" />
                      </div>
                    </div>
                  ))
                : paginated.map((student) => (
                    <motion.div
                      key={student.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className={`p-4 hover:bg-gray-50 ${selectedStudents.includes(student.id) ? 'bg-blue-50' : ''}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                            <GraduationCap className="text-blue-600" size={20} />
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-gray-900 truncate">
                              {student.firstName || student.first_name} {student.lastName || student.last_name}
                            </div>
                            <div className="text-xs text-gray-500 truncate">{student.ref || student.id}</div>
                          </div>
                        </div>
                        <StatusBadge status={student.status} />
                      </div>
                      <div className="mt-2.5 ml-[52px] space-y-1.5">
                        {(student.grade_level ||
                          student.class_name ||
                          student.class ||
                          student.className ||
                          student.grade) && (
                          <div className="flex items-center gap-1.5">
                            <GraduationCap size={13} className="text-gray-400 shrink-0" />
                            <span className="text-sm text-gray-600">
                              {student.grade_level && student.class_name
                                ? `${student.grade_level} ${student.class_name}`
                                : student.class_name ||
                                  student.class ||
                                  student.className ||
                                  student.grade_level ||
                                  student.grade}
                            </span>
                          </div>
                        )}
                        {(student.address?.pickup?.address || student.pickup_address) && (
                          <div className="flex items-start gap-1.5">
                            <Home size={13} className="text-gray-400 shrink-0 mt-0.5" />
                            <span className="text-sm text-gray-600 line-clamp-1">
                              {student.address?.pickup?.address || student.pickup_address}
                            </span>
                          </div>
                        )}
                        {(student.assignedRoute?.route_name || student.assignedStop?.name || student.stop_name) && (
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <Route size={13} className="text-gray-400 shrink-0" />
                            {student.assignedRoute?.route_name && (
                              <span className="text-sm text-gray-600 truncate max-w-[140px]">
                                {student.assignedRoute.route_name}
                              </span>
                            )}
                            {(student.assignedStop?.name || student.stop_name) && (
                              <span className="text-xs text-gray-400">
                                · {student.assignedStop?.name || student.stop_name}
                              </span>
                            )}
                          </div>
                        )}
                        {(student.primaryParent?.name || student.parent_name) && (
                          <div className="flex items-center gap-1.5">
                            <User size={13} className="text-gray-400 shrink-0" />
                            <span className="text-sm text-gray-600 truncate max-w-[220px]">
                              {student.primaryParent?.name || student.parent_name}
                              {(student.primaryParent?.phone || student.parent_phone) &&
                                ` · ${student.primaryParent?.phone || student.parent_phone}`}
                            </span>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
            </div>

            {/* ── Tableau (desktop ≥ md) ── */}
            <div className="overflow-x-auto hidden md:block">
              <table className="min-w-[640px] w-full table-fixed">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={filteredStudents.length > 0 && selectedStudents.length === filteredStudents.length}
                        onChange={toggleSelectAll}
                        className="rounded accent-blue-600 cursor-pointer"
                      />
                    </th>
                    <SortableHeader col="firstName|first_name" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort}>
                      Élève
                    </SortableHeader>
                    {visible('classe') && (
                      <SortableHeader col="grade_level" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort}>
                        Classe
                      </SortableHeader>
                    )}
                    {visible('adresse') && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Adresse
                      </th>
                    )}
                    {visible('transport') && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Transport
                      </th>
                    )}
                    {visible('contact') && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact
                      </th>
                    )}
                    {visible('status') && (
                      <SortableHeader
                        col="status"
                        sortCol={sortCol}
                        sortDir={sortDir}
                        onSort={toggleSort}
                        className="w-28 whitespace-nowrap"
                      >
                        Statut
                      </SortableHeader>
                    )}
                    <th className="px-4 py-3 w-28 whitespace-nowrap text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading &&
                    Array.from({ length: 7 }).map((_, i) => (
                      <tr key={`skel-${i}`}>
                        <td className="px-4 py-4 w-10">
                          <Skeleton className="h-4 w-4 rounded" />
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <Skeleton className="h-10 w-10 rounded-full" />
                            <div className="space-y-1.5">
                              <Skeleton className="h-3.5 w-28" />
                              <Skeleton className="h-3 w-20" />
                            </div>
                          </div>
                        </td>
                        {visible('classe') && (
                          <td className="px-4 py-4">
                            <Skeleton className="h-3.5 w-20" />
                          </td>
                        )}
                        {visible('adresse') && (
                          <td className="px-4 py-4">
                            <Skeleton className="h-3.5 w-32" />
                          </td>
                        )}
                        {visible('transport') && (
                          <td className="px-4 py-4">
                            <div className="space-y-1.5">
                              <Skeleton className="h-3.5 w-24" />
                              <Skeleton className="h-3 w-20" />
                            </div>
                          </td>
                        )}
                        {visible('contact') && (
                          <td className="px-4 py-4">
                            <div className="space-y-1.5">
                              <Skeleton className="h-3.5 w-28" />
                              <Skeleton className="h-3 w-20" />
                            </div>
                          </td>
                        )}
                        {visible('status') && (
                          <td className="px-4 py-4">
                            <Skeleton className="h-5 w-14 rounded-full" />
                          </td>
                        )}
                        <td className="px-4 py-4">
                          <div className="flex justify-end">
                            <Skeleton className="h-7 w-7 rounded" />
                          </div>
                        </td>
                      </tr>
                    ))}
                  {!loading &&
                    paginated.map((student) => (
                      <motion.tr
                        key={student.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className={`hover:bg-gray-50 ${selectedStudents.includes(student.id) ? 'bg-blue-50' : ''}`}
                      >
                        <td className="px-4 py-4 w-10">
                          <input
                            type="checkbox"
                            checked={selectedStudents.includes(student.id)}
                            onChange={() => toggleSelectStudent(student.id)}
                            className="rounded accent-blue-600 cursor-pointer"
                          />
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center">
                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                              <GraduationCap className="text-blue-600" size={20} />
                            </div>
                            <div className="ml-4 min-w-0 flex-1">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {student.firstName || student.first_name} {student.lastName || student.last_name}
                              </div>
                              <div className="text-xs text-gray-500 truncate">{student.ref || student.id}</div>
                            </div>
                          </div>
                        </td>

                        {visible('classe') && (
                          <td className="px-4 py-4">
                            <div className="text-sm text-gray-900 truncate">
                              {student.grade_level && student.class_name
                                ? `${student.grade_level} ${student.class_name}`
                                : student.class_name ||
                                  student.class ||
                                  student.className ||
                                  student.grade_level ||
                                  student.grade || <span className="text-gray-400">-</span>}
                            </div>
                          </td>
                        )}

                        {visible('adresse') && (
                          <td className="px-4 py-4">
                            {student.address?.pickup?.address || student.pickup_address ? (
                              <div className="flex items-start gap-1">
                                <Home size={14} className="text-gray-400 shrink-0 mt-1" />
                                <div className="line-clamp-2 text-sm text-gray-900 leading-relaxed">
                                  {student.address?.pickup?.address || student.pickup_address}
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </td>
                        )}

                        {visible('transport') && (
                          <td className="px-4 py-4">
                            <div className="space-y-1">
                              {(() => {
                                const hasStopId = student.transport?.assigned_stop_id || student.assigned_stop_id;
                                const hasAddress = student.address?.pickup?.address || student.pickup_address;
                                const routeName = student.assignedRoute?.route_name;
                                if (hasStopId && routeName) {
                                  return (
                                    <div className="flex items-center gap-2">
                                      <Route size={14} className="text-gray-400 shrink-0" />
                                      <span className="text-sm text-gray-900 truncate">{routeName}</span>
                                    </div>
                                  );
                                } else if (hasStopId || hasAddress) {
                                  return (
                                    <div className="flex items-center gap-2">
                                      <AlertTriangle size={14} className="text-orange-500 shrink-0" />
                                      <span className="text-sm text-gray-900">En attente</span>
                                    </div>
                                  );
                                }
                                return (
                                  <div className="flex items-center gap-2">
                                    <AlertTriangle size={14} className="text-orange-500 shrink-0" />
                                    <span className="text-sm text-gray-900">Pas de circuit assigné</span>
                                  </div>
                                );
                              })()}
                              {student.assignedStop?.name || student.stop_name ? (
                                <div className="flex items-center gap-2">
                                  <MapPin size={14} className="text-gray-400 shrink-0" />
                                  <span className="text-sm text-gray-900 truncate">
                                    {student.assignedStop?.name || student.stop_name}
                                  </span>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <AlertTriangle size={14} className="text-orange-500 shrink-0" />
                                  <span className="text-sm text-gray-900">Pas d'arrêt assigné</span>
                                </div>
                              )}
                            </div>
                          </td>
                        )}

                        {visible('contact') && (
                          <td className="px-4 py-4">
                            <div className="text-sm text-gray-900">
                              {(student.primaryParent?.name || student.parent_name) && (
                                <div className="flex items-center mb-1">
                                  <User size={14} className="mr-2 text-gray-400" />
                                  <span className="truncate">{student.primaryParent?.name || student.parent_name}</span>
                                </div>
                              )}
                              {(student.primaryParent?.phone || student.parent_phone) && (
                                <div className="flex items-center">
                                  <Phone size={14} className="mr-2 text-gray-400" />
                                  <span className="truncate">
                                    {student.primaryParent?.phone || student.parent_phone}
                                  </span>
                                </div>
                              )}
                              {!student.primaryParent && !student.parent_name && (
                                <span className="text-gray-400">-</span>
                              )}
                            </div>
                          </td>
                        )}

                        {visible('status') && (
                          <td className="px-4 py-4 whitespace-nowrap">
                            <StatusBadge status={student.status} />
                          </td>
                        )}

                        <td className="px-4 py-4 whitespace-nowrap text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                aria-label="Actions"
                                className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                              >
                                <MoreHorizontal size={16} />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-40">
                              <DropdownMenuItem onClick={() => handleEditStudent(student)}>
                                <Edit size={14} className="mr-2" />
                                Modifier
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleDeleteStudent(student)}
                                className="text-red-600 focus:text-red-600 focus:bg-red-50"
                              >
                                <Trash2 size={14} className="mr-2" />
                                Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </motion.tr>
                    ))}
                </tbody>
              </table>
            </div>
            {/* ── Fin desktop ── */}

            {!loading && filteredStudents.length > 0 && (
              <TablePagination
                page={page}
                totalPages={totalPages}
                totalItems={totalItems}
                pageSize={pageSize}
                onPageChange={setPage}
              />
            )}

            {!loading && filteredStudents.length === 0 && (
              <div className="text-center py-12">
                <Users className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun élève trouvé</h3>
                <p className="mt-1 text-sm text-gray-500 mb-4">
                  {filters.search || filters.status || filters.grade
                    ? 'Aucun élève ne correspond aux filtres sélectionnés.'
                    : 'Commencez par ajouter un élève.'}
                </p>
                {!(filters.search || filters.status || filters.grade) && (
                  <Button onClick={handleAddStudent}>
                    <UserPlus size={16} />
                    Ajouter un élève
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <StudentParentFormModal
        isOpen={showStudentModal}
        onClose={() => setShowStudentModal(false)}
        onSubmit={handleStudentSubmit}
        student={selectedStudent}
        mode={modalMode}
      />

      {showImportModal && (
        <AdvancedCSVImportModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onImportComplete={handleImportComplete}
        />
      )}

      {showImpactModal && selectedStudentForAction && impactAction && (
        <StudentImpactConfirmationModal
          student={selectedStudentForAction}
          action={impactAction}
          onConfirm={executeAction}
          onCancel={cancelImpactModal}
        />
      )}

      {showBulkDeleteModal && (
        <BulkDeleteImpactModal
          students={students.filter((s) => selectedStudents.includes(s.id))}
          onConfirm={confirmBulkDelete}
          onCancel={cancelBulkDeleteModal}
        />
      )}

      <AnimatePresence>
        {selectedStudents.length > 0 && (
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="hidden md:flex fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white rounded-xl shadow-2xl px-5 py-3 items-center gap-3"
          >
            <span className="text-sm font-medium whitespace-nowrap">
              {selectedStudents.length} élève(s) sélectionné(s)
            </span>
            <div className="h-4 w-px bg-gray-600" />
            <select
              value={bulkAction}
              onChange={(e) => setBulkAction(e.target.value)}
              className="bg-gray-800 text-white text-sm rounded-lg px-3 py-1.5 border border-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
            >
              <option value="">-- Action --</option>
              <option value="activate">Activer</option>
              <option value="deactivate">Désactiver</option>
              <option value="delete">Supprimer</option>
            </select>
            <Button
              size="sm"
              disabled={!bulkAction || bulkLoading}
              onClick={handleBulkAction}
              className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
            >
              {bulkLoading ? 'En cours...' : 'Appliquer'}
            </Button>
            <button
              onClick={() => {
                setSelectedStudents([]);
                setBulkAction('');
              }}
              className="text-gray-400 hover:text-white transition-colors ml-1"
              title="Désélectionner tout"
            >
              <X size={16} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StudentManagementEnhanced;
